import {createContext, useContext, useLayoutEffect, useState, useRef} from "react";
import type {ReactNode} from "react";
import {auth, firebaseAnalytics, isAndroid, AnalyticsTrackerPlugin} from "FirebaseUtils";
import {onAuthStateChanged} from "firebase/auth";
import {FirebaseCrashlytics} from "@capacitor-firebase/crashlytics";
import {FirebaseAnalytics} from "@capacitor-firebase/analytics";
import {setUserId} from "firebase/analytics";
import {FirebaseAuthentication} from "@capacitor-firebase/authentication";
import LogService from "services/LogService";
import {registerPlugin} from "@capacitor/core";
import AuthService from "services/AuthService";

const SessionManagerPlugin = registerPlugin<any>("SessionManagerPlugin");
const AuthContext = createContext<any>(undefined);

interface Props {
  children: ReactNode;
}

function AuthProvider({children}: Props) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isGettingClaims, setIsGettingClaims] = useState(true);
  const tokenListenerRef = useRef(null);
  const authListenerRef = useRef(null);
  const freshTokenRequestedRef = useRef(false);

  // A rejected custom token is usually one native minted too long ago. Ask for a fresh one
  // once; a second failure means something else is wrong and we stay signed out.
  const handleCustomTokenFailure = (reason: any) => {
    LogService.addInfoLogAsync("", "Login with custom error " + reason, false, true);
    setUser(null);
    setIsGettingClaims(false);
    setIsAuthenticating(false);
    if (freshTokenRequestedRef.current) return;
    freshTokenRequestedRef.current = true;
    LogService.addInfoLogAsync("", "Requesting a fresh custom token from native", false, true);
    SessionManagerPlugin.requestCustomToken().catch((error) => {
      LogService.addInfoLogAsync("", "requestCustomToken failed " + error, false, true);
    });
  };

  useLayoutEffect(() => {
    let unsubscribe;
    LogService.addInfoLogAsync("", "AuthProvider useLayoutEffect", false, true);
    if (isAndroid) {
      LogService.addInfoLogAsync("", "AuthProvider useLayoutEffect isAndroid", false, true);

      const setupListeners = async () => {
        try {
          // Remove existing listeners if they exist
          if (tokenListenerRef.current) {
            LogService.addInfoLogAsync("", "Removing existing token listener", false, true);
            tokenListenerRef.current.remove();
            tokenListenerRef.current = null;
          }

          if (authListenerRef.current) {
            LogService.addInfoLogAsync("", "Removing existing auth listener", false, true);
            authListenerRef.current.remove();
            authListenerRef.current = null;
          }

          // Add a small delay to ensure plugin is initialized
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Listen for custom token from native code
          tokenListenerRef.current = await SessionManagerPlugin.addListener("customTokenReceived", async ({token}) => {
            try {
              const result = await AuthService.signInWithCustomToken(token);
              if (result.isSuccess) {
                freshTokenRequestedRef.current = false;
                await authStateChanged(result.data.user);
              } else {
                handleCustomTokenFailure(result.error);
              }
            } catch (error) {
              handleCustomTokenFailure(error);
            }
          });

          authListenerRef.current = await FirebaseAuthentication.addListener("authStateChange", async ({user}) => {
            await authStateChanged(user, true);
          });
        } catch (error) {
          LogService.addInfoLogAsync("", "Error setting up listeners: " + error, false, true);
        }
      };

      // Setup listeners with retry mechanism
      const setupWithRetry = async (retryCount = 0) => {
        try {
          await setupListeners();
        } catch (error) {
          if (retryCount < 3) {
            LogService.addInfoLogAsync("", `Retrying listener setup (${retryCount + 1}/3)`, false, true);
            setTimeout(() => setupWithRetry(retryCount + 1), 500);
          } else {
            LogService.addInfoLogAsync("", "Failed to setup listeners after 3 retries", false, true);
          }
        }
      };

      setupWithRetry();

      unsubscribe = () => {
        LogService.addInfoLogAsync("", "Login authStateChanged unsubscribe", false, true);
        if (tokenListenerRef.current) {
          tokenListenerRef.current.remove();
          tokenListenerRef.current = null;
        }
        if (authListenerRef.current) {
          authListenerRef.current.remove();
          authListenerRef.current = null;
        }
      };
    } else {
      LogService.addInfoLogAsync("", "AuthProvider useLayoutEffect else", false, true);
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        await authStateChanged(user);
      });
    }
    return () => unsubscribe();
  }, []);

  const authStateChanged = async (user, fromListener = false) => {
    setIsAuthenticating(true);
    const isUserSignOut = user === null;
    if (isUserSignOut) {
      setUser(null);
      setIsGettingClaims(false);
    } else {
      // A sign-in after a signed-out start: routes gated on claims (admin) must
      // wait for this user's, not read the empty ones left by the signed-out state.
      setIsGettingClaims(true);
      if (isAndroid) {
        await FirebaseCrashlytics.setUserId({
          userId: user.uid
        });
        await FirebaseAnalytics.setUserId({
          userId: user.uid
        });
        LogService.pushAndroidLogs(user.uid, true);
      } else {
        await AnalyticsTrackerPlugin.initUser(user.uid);
        setUserId(firebaseAnalytics, user.uid);
      }
      setUser(user);

      if (isAndroid) {
        FirebaseAuthentication.getIdToken()
          .then(({idTokenResult}: any) => {
            if (fromListener) {
              AuthService.signInWithCustomToken(idTokenResult?.token);
            }
            setClaims(idTokenResult?.claims);
          })
          .catch((error) => {
            LogService.addInfoLogAsync("", "Error getting idTokenResult error: " + error, false, true);
            console.error("Error getting idTokenResult", error);
          })
          .finally(() => {
            setIsGettingClaims(false);
          });
      } else {
        user
          .getIdTokenResult()
          .then((idTokenResult) => {
            setClaims(idTokenResult.claims);
          })
          .catch((error) => {
            console.error("Error getting idTokenResult", error);
          })
          .finally(() => {
            setIsGettingClaims(false);
          });
      }
    }
    setIsAuthenticating(false);
  };

  const values = {
    user,
    claims,
    isAuthenticating,
    setIsAuthenticating,
    isGettingClaims,
    setIsGettingClaims
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export {AuthProvider};
